"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const processSteps = [
  {
    icon: "/hong-kong-accounting-services/card-1.svg",
    iconClassName: "h-16 w-16",
    title: "HKICPA Qualified, Not Outsourced",
    description:
      "When you outsource accounting in Hong Kong to MuteTaxes, the work stays in house to our own HKICPA qualified accountants rather than being coordinated through a third party.",
    contentClassName: "pb-[37px]",
    descriptionClassName: "text-[#a0a3b2]",
  },
  {
    icon: "/hong-kong-accounting-services/card-2.svg",
    iconClassName:
      "h-16 w-16 backdrop-blur-[2px] backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(2px)_brightness(100%)]",
    title: "A Dashboard You Can Actually Read",
    description:
      "Your monthly performance and full transaction listing are kept in a dashboard you can open at any time, not locked inside software you were never trained on.",
   contentClassName: "pb-[37px]",
    titleClassName: "max-w-[250px]",
     descriptionClassName: "max-w-[254px]",
  },
  {
    icon: "/hong-kong-accounting-services/card-3.svg",
    iconClassName: "h-16 w-16",
    title: "A Senior Lead, Not a Ticket Queue",
    description:
      "Your account is supported by a dedicated back end team, with a department head and senior lead as your front line contact whenever you need proper guidance.",
    contentClassName: "pb-[37px]",
    titleClassName: " max-w-[250px]",
        descriptionClassName: "max-w-[254px]",
  },
  {
    icon: "/hong-kong-accounting-services/card-4.svg",
    iconClassName: "h-[66px] w-[66px]",
    title: "Built for Multi Platform Businesses",
    description:
      "Marketplace payouts, processor fees, refunds and more than one currency in the same month are ordinary for the businesses we work with.",
   contentClassName: "pb-[37px]",
    titleClassName: "sm:max-w-[250px]",
    descriptionClassName: "max-w-[254px]",
  },
];




const cardsContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
  },
};

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

export const FounderTrustBenefitsSection = (): JSX.Element => {
  return (
    <section
      aria-labelledby="how-it-works-heading"
      className="flex w-full flex-col mt-20 sm:mt-0 items-center max-sm:gap-[43px] gap-[83px] px-4 py-0"
    >
      <header className="flex w-full flex-col items-center gap-6 pt-6">
        <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(3.5px)_brightness(100%)]">
         <span className="h-[6px] w-[6px] rounded-full bg-[#70A1FF] mr-2"></span> Why MuteTaxes
        </Badge>
        <motion.h2
          id="how-it-works-heading"
          className="w-full [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-4xl font-normal leading-[1.2] tracking-[-1.92px] text-white sm:text-5xl sm:leading-[68px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          Why Founders Trust MuteTaxes for <br className="hidden sm:block" />
Hong Kong Accounting Services
        </motion.h2>
      </header>
      <div className="flex w-full max-w-[1408px] flex-col items-center gap-6">
   
        <motion.div
          className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
          variants={cardsContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
        >
          {processSteps.map((step) => (
            <motion.div key={step.title} variants={cardVariants}>
              <Card className="relative sm:min-h-[340px] overflow-hidden rounded-[32px] border-0 bg-[#0f1319] shadow-none">
                <span className="absolute -right-[76px] -top-[49px] h-[171px] w-[171px] rounded-full bg-[#70a6ff] blur-[71.73px]" />
                <span className="absolute -right-[76px] -top-[49px] h-[105px] w-[171px] rounded-full bg-[#d3e4ff] blur-[71.73px]" />
                <span className="absolute -right-[76px] -top-[49px] h-[89px] w-[171px] rounded-full bg-white blur-[37px]" />
                <span className="absolute bottom-[-94px] left-[9%] h-72 w-[91%] rounded-[50%] bg-[#0061fe] blur-[75px]" />
                <CardContent
                  className={`relative flex h-full flex-col items-start gap-6 sm:px-5 py-5 sm:pt-10 ${step.contentClassName}`}
                >
                  <img
                    className={step.iconClassName}
                    alt=""
                    aria-hidden="true"
                    src={step.icon}
                  />
                  <div className="flex flex-col items-start gap-4">
                    <h3
                      className={`[font-family:'Suisse_Intl_Trial-Regular',Helvetica] font-bold text-xl sm:text-2xl leading-[28.8px] tracking-[-0.24px] text-white ${step.titleClassName ?? ""}`}
                    >
                      {step.title}
                    </h3>
                    <p
                      className={`[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-base font-normal leading-[22.4px] tracking-[-0.16px] text-[#a0a4b3] ${step.descriptionClassName ?? ""}`}
                    >
                      {step.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
